import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo } from 'react';
import { IAnalytics, LocalAnalytics, PosthogAnalytics } from '@/common/telemetry';
import useMainStore from '@/store/mainStore';

/**
 * Hook that provides an IAnalytics-compliant analytics interface.
 * Returns PosthogAnalytics when PostHog is enabled, otherwise LocalAnalytics.
 *
 * @example
 * ```tsx
 * const analytics = useAnalytics();
 *
 * // Track an event
 * analytics.capture({
 *   event: TELEMETRY_MAPPING.BUY_NOW_BUTTON_CLICK,
 *   properties: { ... }
 * });
 *
 * // Identify a user
 * analytics.identify({
 *   distinctId: 'user123',
 *   properties: { user_wallet: '0x...' }
 * });
 * ```
 */
export function useAnalytics(): IAnalytics {
  const posthog = usePostHog();
  const setAnalytics = useMainStore((state) => state.setAnalytics);
  const analytics = useMainStore((state) => state.analytics);

  // Create analytics object without updating state
  const memoizedAnalytics = useMemo(() => {
    if (analytics) {
      return analytics;
    }

    if (process.env.PUBLIC_POSTHOG_KEY && process.env.PUBLIC_POSTHOG_HOST && posthog) {
      return new PosthogAnalytics(posthog);
    }

    return new LocalAnalytics();
  }, [posthog, analytics]);

  // Update the store with the analytics object in an effect
  useEffect(() => {
    setAnalytics(memoizedAnalytics);
  }, [memoizedAnalytics, setAnalytics]);

  return memoizedAnalytics;
}
